import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, FileText, FileSpreadsheet, Printer, Download, Folder, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only modal to preview & print a target user's expense files
 * without entering impersonation mode.
 * Uses per-request X-Impersonate header override.
 */
export default function AdminUserFilesModal({ user, onClose }) {
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [month, setMonth] = useState("");
  const [reportId, setReportId] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const reqCfg = useMemo(
    () => ({ headers: { "X-Impersonate": user.user_id } }),
    [user.user_id]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoadingData(true);
      // Load independently so one failure doesn't blank the whole modal
      const [repRes, invRes] = await Promise.allSettled([
        api.get("/reports", reqCfg),
        api.get("/invoices", reqCfg),
      ]);
      if (!active) return;
      if (repRes.status === "fulfilled") setReports(repRes.value.data || []);
      else {
        const d = repRes.reason?.response?.data?.detail || repRes.reason?.message;
        console.error("Admin files modal: /reports failed", repRes.reason);
        toast.error(`Rapports: ${typeof d === "string" ? d : "erreur"}`);
      }
      if (invRes.status === "fulfilled") setInvoices(invRes.value.data || []);
      else {
        const d = invRes.reason?.response?.data?.detail || invRes.reason?.message;
        console.error("Admin files modal: /invoices failed", invRes.reason);
        toast.error(`Factures: ${typeof d === "string" ? d : "erreur"}`);
      }
      setLoadingData(false);
    };
    load();
    return () => { active = false; };
  }, [reqCfg]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const activeReport = reports.find((r) => r.report_id === reportId) || null;
  const scopeLabel = activeReport?.name || month || "Tous";

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    if (reportId) list = list.filter((i) => i.report_id === reportId);
    else if (month) list = list.filter((i) => (i.date || "").startsWith(month));
    return [...list].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [invoices, reportId, month]);

  const total = filteredInvoices.reduce((s, i) => s + (Number(i.total) || 0), 0);

  const months = useMemo(() => {
    const set = new Set();
    invoices.forEach((i) => { if (i.date) set.add(String(i.date).slice(0, 7)); });
    return [...set].sort().reverse();
  }, [invoices]);

  const payload = () => ({
    month: month || undefined,
    report_id: reportId || undefined,
    company: "LABORATOIRE RIVA",
  });

  const loadPdf = async () => {
    if (!filteredInvoices.length) { toast.error("Aucune facture"); return; }
    setLoadingPdf(true);
    try {
      const res = await api.post("/export/pdf", payload(), { ...reqCfg, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfBlob(blob);
      // Auto-open in a new tab for reliable rendering on desktop + mobile
      const w = window.open(url, "_blank");
      if (!w) toast.info("Activez les popups pour ouvrir le PDF automatiquement");
    } catch (e) {
      // Parse blob error response
      let detail = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          detail = j.detail;
        } catch { /* ignore */ }
      }
      toast.error(detail || "Impossible de générer le PDF");
    } finally {
      setLoadingPdf(false);
    }
  };

  const openPdfInNewTab = () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, "_blank");
  };

  const downloadPdf = () => {
    if (!pdfBlob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(pdfBlob);
    a.download = `factures_${user.name || "user"}_${scopeLabel}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const printPdf = () => {
    if (!pdfUrl) { toast.info("Chargez l'aperçu PDF d'abord"); return; }
    const w = window.open(pdfUrl, "_blank");
    if (!w) { toast.error("Activez les popups pour imprimer"); return; }
    // Give PDF a moment to render, then trigger print
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* browser blocks some cross-origin */ } }, 800);
  };

  const downloadExcel = async () => {
    if (!filteredInvoices.length) { toast.error("Aucune facture"); return; }
    setLoadingExcel(true);
    try {
      const res = await api.post("/export/excel", payload(), { ...reqCfg, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `compte_depenses_${user.name || "user"}_${scopeLabel}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel indisponible");
    } finally {
      setLoadingExcel(false);
    }
  };

  const printInvoiceTable = () => {
    if (!filteredInvoices.length) { toast.error("Aucune facture"); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Activez les popups"); return; }
    const rows = filteredInvoices.map((i, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${i.date || ""}</td>
        <td>${(i.merchant || i.description || "").replace(/</g, "&lt;")}</td>
        <td>${(i.participants || "").replace(/</g, "&lt;")}</td>
        <td>${i.province || ""}</td>
        <td style="text-align:right">${(Number(i.total) || 0).toFixed(2)} $</td>
      </tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Compte de dépenses — ${user.name}</title>
      <style>
        body{font-family:'Helvetica',Arial,sans-serif;padding:28px;color:#0B0F19}
        h1{font-size:20px;margin:0 0 4px}
        .meta{color:#6B7280;font-size:12px;margin-bottom:18px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border-bottom:1px solid #E5E7EB;padding:8px 6px;text-align:left}
        th{background:#F3F4F6;font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:.04em}
        tfoot td{font-weight:700;border-top:2px solid #0B0F19}
      </style></head><body>
      <h1>Compte de dépenses — ${user.name}</h1>
      <div class="meta">${user.email} · Période: ${scopeLabel} · ${filteredInvoices.length} facture(s)</div>
      <table>
        <thead><tr><th>#</th><th>Date</th><th>Description</th><th>Participant(s)</th><th>Prov.</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan="5" style="text-align:right">TOTAL</td><td style="text-align:right">${total.toFixed(2)} $</td></tr></tfoot>
      </table>
      <script>window.addEventListener('load',()=>setTimeout(()=>window.print(),300));<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-stretch p-2 sm:p-4 md:p-8"
      data-testid="admin-user-files-modal"
    >
      <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full flex flex-col max-h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-neutral-700 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {user.picture
              ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full border border-gray-200" />
              : <div className="w-10 h-10 rounded-full bg-gray-200" />}
            <div className="min-w-0">
              <p className="overline">Fichiers utilisateur</p>
              <div className="font-heading font-black text-xl sm:text-2xl tracking-tight truncate">{user.name}</div>
              <div className="text-xs text-gray-500 truncate">{user.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost shrink-0" data-testid="admin-files-close">
            <X size={16} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-end gap-3 p-4 border-b border-gray-200 dark:border-neutral-700">
          <div>
            <label className="ra-label">Rapport</label>
            <select
              className="ra-input !min-w-[180px]"
              value={reportId}
              onChange={(e) => { setReportId(e.target.value); setMonth(""); }}
              data-testid="admin-files-report-select"
            >
              <option value="">— Aucun —</option>
              {reports.map((r) => (
                <option key={r.report_id} value={r.report_id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="ra-label">Mois</label>
            <select
              className="ra-input !min-w-[140px]"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setReportId(""); }}
              disabled={!!reportId}
              data-testid="admin-files-month-select"
            >
              <option value="">Tous</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="grow" />
          <div className="flex flex-wrap gap-2">
            <button
              className="btn-secondary"
              onClick={loadPdf}
              disabled={loadingPdf}
              data-testid="admin-files-load-pdf"
            >
              <FileText size={14} /> {loadingPdf ? "Chargement..." : "Voir PDF"}
            </button>
            <button
              className="btn-secondary"
              onClick={printPdf}
              disabled={!pdfUrl}
              title={pdfUrl ? "Imprimer le PDF" : "Chargez l'aperçu PDF d'abord"}
              data-testid="admin-files-print-pdf"
            >
              <Printer size={14} /> Imprimer PDF
            </button>
            <button
              className="btn-secondary"
              onClick={printInvoiceTable}
              data-testid="admin-files-print-table"
            >
              <Printer size={14} /> Imprimer la liste
            </button>
            <button
              className="btn-primary"
              onClick={downloadExcel}
              disabled={loadingExcel}
              data-testid="admin-files-download-excel"
            >
              <FileSpreadsheet size={14} /> {loadingExcel ? "..." : "Excel"}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 grid md:grid-cols-[380px_1fr]">
          {/* Invoices summary */}
          <div className="border-r border-gray-200 dark:border-neutral-700 overflow-auto">
            <div className="p-4 border-b border-gray-200 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500">Factures</div>
                <div className="font-heading font-black text-lg num" data-testid="admin-files-invoice-count">
                  {filteredInvoices.length}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 text-right">Total</div>
                <div className="font-heading font-black text-lg num" data-testid="admin-files-total">
                  {total.toFixed(2)} $
                </div>
              </div>
            </div>
            {loadingData ? (
              <div className="p-8 text-center text-gray-400">Chargement...</div>
            ) : filteredInvoices.length === 0 ? (
              <div className="p-8 text-center text-gray-400 flex flex-col items-center gap-2">
                <Folder size={28} className="opacity-40" />
                <div>Aucune facture</div>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-neutral-800">
                {filteredInvoices.map((inv) => (
                  <li key={inv.invoice_id} className="p-3 text-sm" data-testid={`admin-files-inv-${inv.invoice_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{inv.merchant || inv.description || "—"}</div>
                        <div className="text-xs text-gray-500">{inv.date || "Sans date"} · {inv.province || ""}</div>
                      </div>
                      <div className="num font-semibold shrink-0">{(Number(inv.total) || 0).toFixed(2)} $</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* PDF Panel */}
          <div className="flex flex-col min-h-[300px] bg-gray-50 dark:bg-neutral-950">
            {pdfUrl ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-3">
                <FileText size={56} className="text-[var(--ra-primary)]" />
                <div>
                  <div className="font-heading font-black text-xl tracking-tight">PDF prêt</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {filteredInvoices.filter((i) => i.image_filename).length} page(s) — {scopeLabel}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  <button className="btn-primary" onClick={openPdfInNewTab} data-testid="admin-files-open-pdf">
                    <ExternalLink size={14} /> Ouvrir dans un nouvel onglet
                  </button>
                  <button className="btn-secondary" onClick={downloadPdf} data-testid="admin-files-download-pdf">
                    <Download size={14} /> Télécharger
                  </button>
                </div>
                <div className="text-xs text-gray-400 mt-3 max-w-xs">
                  L'aperçu PDF s'ouvre dans un nouvel onglet pour une compatibilité optimale (mobile + desktop).
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                <FileText size={48} className="opacity-30 mb-3" />
                <div className="text-sm">Cliquez sur <span className="font-semibold">Aperçu PDF</span> pour ouvrir les factures de cet utilisateur.</div>
                <div className="text-xs mt-1">Nécessite au moins une facture avec image.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
